// src/app/core/services/theme.service.ts
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export type ThemeMode = "light" | "dark" | "system";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private readonly THEME_KEY = "theme";

  // BehaviorSubject to track current theme
  private themeSubject = new BehaviorSubject<ThemeMode>("system");
  private isDarkModeSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public theme$: Observable<ThemeMode> = this.themeSubject.asObservable();
  public isDarkMode$: Observable<boolean> =
    this.isDarkModeSubject.asObservable();

  // Current theme getters
  get currentTheme(): ThemeMode {
    return this.themeSubject.value;
  }

  get isDarkMode(): boolean {
    return this.isDarkModeSubject.value;
  }

  constructor() {
    this.initializeTheme();
    this.setupSystemThemeListener();
  }

  /**
   * Initialize theme from localStorage or default to system
   */
  private initializeTheme(): void {
    const savedTheme =
      (localStorage.getItem(this.THEME_KEY) as ThemeMode) || "system";
    this.applyTheme(savedTheme);
  }

  /**
   * Listen for system theme changes
   */
  private setupSystemThemeListener(): void {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    mediaQuery.addEventListener("change", () => {
      if (this.currentTheme === "system") {
        this.updateThemeState();
      }
    });
  }

  /**
   * Apply theme - main method for theme switching
   */
  applyTheme(theme: ThemeMode): void {
    this.themeSubject.next(theme);
    localStorage.setItem(this.THEME_KEY, theme);
    this.updateThemeState();
  }

  /**
   * Update theme state and DOM
   */
  private updateThemeState(): void {
    const htmlElement = document.documentElement;
    let isDark = false;

    switch (this.currentTheme) {
      case "dark":
        htmlElement.classList.add("dark");
        htmlElement.classList.remove("light");
        isDark = true;
        break;
      case "light":
        htmlElement.classList.remove("dark");
        htmlElement.classList.add("light");
        isDark = false;
        break;
      case "system":
        const systemPrefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
        if (systemPrefersDark) {
          htmlElement.classList.add("dark");
          htmlElement.classList.remove("light");
          isDark = true;
        } else {
          htmlElement.classList.remove("dark");
          htmlElement.classList.add("light");
          isDark = false;
        }
        break;
    }

    this.isDarkModeSubject.next(isDark);
  }

  /**
   * Set specific theme
   */
  setTheme(theme: ThemeMode): void {
    this.applyTheme(theme);
  }

  /**
   * Get theme icon class
   */
  getThemeIcon(theme?: ThemeMode): string {
    const targetTheme = theme || this.currentTheme;

    switch (targetTheme) {
      case "light":
        return "pi pi-sun";
      case "dark":
        return "pi pi-moon";
      case "system":
        return "pi pi-desktop";
      default:
        return "pi pi-desktop";
    }
  }

  /**
   * Get theme label
   */
  getThemeLabel(theme?: ThemeMode): string {
    const targetTheme = theme || this.currentTheme;

    switch (targetTheme) {
      case "light":
        return "Light";
      case "dark":
        return "Dark";
      case "system":
        return "System";
      default:
        return "System";
    }
  }
}
